import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney } from "@/lib/api";
import {
  Users2, Search, Phone, Mail, MessageSquare, MapPin, Plus, Filter,
  Flame, Sparkles, TrendingUp, DollarSign, ArrowUpRight, UserPlus,
  Eye, CheckCircle2, Building2, Zap, X, Send, Reply,
  Share2, QrCode, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import ConvertCaptureModal from "@/components/ConvertCaptureModal";
import BulkBlastModal, { BulkBlastButton } from "@/components/BulkBlastModal";

// ───── helpers ────────────────────────────────────────────────────────────

const FILTER_TABS = [
  { key: "all", label: "All Buyers", icon: Users2 },
  { key: "vetted", label: "Cash Buyers", icon: CheckCircle2 },
  { key: "capture", label: "New Captures", icon: Sparkles },
  { key: "hot", label: "Hot", icon: Flame },
];

const SOURCE_TONE = {
  qr:         { bg: "#10B98122", fg: "#10B981", border: "#10B98155" },
  share_arc:  { bg: "#0EA5E922", fg: "#0EA5E9", border: "#0EA5E955" },
  buyer_nudge:{ bg: "#F59E0B22", fg: "#B45309", border: "#F59E0B55" },
  sms:        { bg: "#F59E0B22", fg: "#B45309", border: "#F59E0B55" },
  direct:     { bg: "#94A3B822", fg: "#475569", border: "#94A3B855" },
};
function sourceTone(s) {
  return SOURCE_TONE[(s || "").toLowerCase()] || { bg: "#6366F122", fg: "#4F46E5", border: "#6366F155" };
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 86400 * 30) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

// Initial-based gradient avatar — 8-stop palette keyed on first letter so each
// buyer gets a stable color across reloads.
const AVATAR_GRADIENTS = [
  ["#10B981", "#059669"], ["#0EA5E9", "#0284C7"], ["#F59E0B", "#D97706"],
  ["#EC4899", "#DB2777"], ["#8B5CF6", "#7C3AED"], ["#EF4444", "#DC2626"],
  ["#14B8A6", "#0D9488"], ["#F97316", "#EA580C"],
];
function gradientForName(name) {
  const ch = (name || "?").trim().toUpperCase().charCodeAt(0) || 63;
  return AVATAR_GRADIENTS[ch % AVATAR_GRADIENTS.length];
}

// ───── main page ──────────────────────────────────────────────────────────

export default function Buyers() {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("hot");
  const [converting, setConverting] = useState(null);
  const [blasting, setBlasting] = useState(false);
  const [blastPerf, setBlastPerf] = useState(null);

  const refresh = useCallback(async () => {
    const params = new URLSearchParams();
    if (tab === "vetted") params.set("kind", "vetted");
    else if (tab === "capture") params.set("kind", "capture");
    else if (tab === "hot") params.set("sort", "hot");
    if (sort && tab !== "hot") params.set("sort", sort);
    if (q.trim()) params.set("q", q.trim());

    const [list, sum, perf] = await Promise.all([
      api.get(`/buyers?${params.toString()}`),
      api.get("/buyers/summary"),
      api.get("/buyers/blast-performance?days=7"),
    ]);
    let data = list.data;
    if (tab === "hot") data = data.filter((r) => (r.engagement_score || 0) >= 60);
    setRows(data);
    setSummary(sum.data);
    setBlastPerf(perf.data);
  }, [tab, q, sort]);

  useEffect(() => { refresh(); }, [refresh]);

  const onConverted = (member) => {
    setConverting(null);
    toast.success(`${member.name} promoted to Cash Buyer`);
    refresh();
  };

  return (
    <div data-testid="buyers-page">
      {/* ── Search/My-List page-level tabs ───────────────────────────── */}
      <div className="flex gap-1 border-b border-[var(--line)] mb-5">
        <Link
          to="/app/buyers/search"
          className="px-4 py-2 text-sm font-bold border-b-2 -mb-px border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]"
          data-testid="page-tab-search"
        >
          <Search size={14} className="inline -mt-0.5 mr-1" /> Search for buyers
        </Link>
        <span
          className="px-4 py-2 text-sm font-bold border-b-2 -mb-px border-emerald-500 text-emerald-700"
          data-testid="page-tab-my-list"
        >
          <Users2 size={14} className="inline -mt-0.5 mr-1" /> My Buyer List
        </span>
      </div>

      {/* ── HERO: gradient header with total buy power ───────────────── */}
      <BuyersHero summary={summary} perf={blastPerf} onBlast={() => setBlasting(true)} />

      {/* ── STAT STRIP ───────────────────────────────────────────────── */}
      {summary && <StatStrip summary={summary} />}

      {/* ── TOOLBAR ──────────────────────────────────────────────────── */}
      <div className="card mb-5 mt-5 flex items-center gap-3 flex-wrap" data-testid="buyers-toolbar">
        <div className="flex items-center gap-2 flex-1 min-w-[240px]">
          <Search size={16} className="text-[var(--ink-3)] shrink-0" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, phone, email, market…"
            className="bg-transparent flex-1 outline-none text-sm"
            data-testid="buyers-search"
          />
          {q && (
            <button type="button" className="btn-ghost" onClick={() => setQ("")}>
              <X size={14} />
            </button>
          )}
        </div>
        <div className="hidden md:flex items-center gap-2 text-xs text-[var(--ink-3)]">
          <Filter size={13} />
          Sort
        </div>
        <select
          className="select max-w-[170px]"
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="buyers-sort"
        >
          <option value="hot">🔥 Hottest first</option>
          <option value="recent">Most recent</option>
          <option value="name">A → Z</option>
        </select>
        <Link to="/app/team" className="btn-outline" data-testid="buyers-add-btn">
          <Plus size={14} /> Add Cash Buyer
        </Link>
      </div>

      {/* ── TABS ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2 mb-6" data-testid="buyers-tabs">
        {FILTER_TABS.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          const count = summary ? (
            t.key === "all" ? summary.total :
            t.key === "vetted" ? summary.vetted :
            t.key === "capture" ? summary.captures_new :
            t.key === "hot" ? summary.hot : null
          ) : null;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setTab(t.key)}
              className={"buyers-tab" + (active ? " active" : "")}
              data-testid={`buyers-tab-${t.key}`}
            >
              <Icon size={14} />
              <span>{t.label}</span>
              {count != null && count > 0 && (
                <span className="buyers-tab-count">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {/* ── CARD GRID ────────────────────────────────────────────────── */}
      {rows === null ? (
        <div className="card text-center py-20 text-[var(--ink-2)]">Loading buyers…</div>
      ) : rows.length === 0 ? (
        <EmptyState tab={tab} q={q} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="buyers-grid">
          {rows.map((b) => (
            <BuyerCard
              key={`${b.kind}-${b.id}`}
              buyer={b}
              onConvert={() => setConverting(b)}
              onChanged={refresh}
            />
          ))}
        </div>
      )}

      {converting && converting.kind === "capture" && (
        <ConvertCaptureModal
          capture={{
            id: converting.id,
            name: converting.name,
            phone: converting.phone,
            email: converting.email,
            message: converting.notes,
            source_lead_id: null,
            source_lead_address: converting.source_lead_address,
            utm_source: converting.utm_source,
          }}
          onClose={() => setConverting(null)}
          onConverted={onConverted}
        />
      )}

      <BulkBlastModal open={blasting} onClose={() => setBlasting(false)} />
    </div>
  );
}

// ───── HERO header ────────────────────────────────────────────────────────

function BuyersHero({ summary, perf, onBlast }) {
  const power = summary?.total_buy_power ?? 0;
  const total = summary?.total ?? 0;
  const hot = summary?.hot ?? 0;
  return (
    <div className="buyers-hero relative overflow-hidden rounded-2xl mb-6" data-testid="buyers-hero">
      <div className="buyers-hero-bg" />
      <div className="buyers-hero-grain" />
      <div className="relative z-10 p-6 md:p-9 grid md:grid-cols-[1fr_auto] gap-6 items-center">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[10px] tracking-widest font-bold uppercase bg-white/10 backdrop-blur border border-white/15 text-white/90">
            <Users2 size={11} /> Buyers CRM
          </div>
          <h1 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight mt-3 text-white leading-[1.05]">
            Your Buyers List
          </h1>
          <p className="text-white/70 text-sm md:text-base mt-2 max-w-xl">
            Every cash buyer, vetted contact, and fresh capture in one place.
            Search instantly, see who&apos;s engaging, match them to your next deal in one click.
          </p>
          <div className="mt-4 flex items-center gap-3 flex-wrap">
            <BulkBlastButton hot={hot} onClick={onBlast} />
            {perf && perf.sent_count > 0 && <BlastPerfStrip perf={perf} />}
          </div>
        </div>
        <div className="buyers-hero-money" data-testid="buyers-hero-money">
          <div className="text-[10px] uppercase tracking-widest text-white/60 font-bold">Total Buy Power</div>
          <div className="font-display font-extrabold text-4xl md:text-5xl text-white mt-1 tabular-nums">
            {power >= 1_000_000
              ? `$${(power / 1_000_000).toFixed(1)}M`
              : `$${Math.round(power / 1000)}K`}
          </div>
          <div className="text-xs text-white/60 mt-1">
            across <span className="font-bold text-white/90">{total}</span> contacts
            {hot > 0 && (
              <> · <span className="text-amber-300 font-bold">{hot} hot 🔥</span></>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ───── BLAST PERFORMANCE STRIP ───────────────────────────────────────────

function BlastPerfStrip({ perf }) {
  const rate = perf.response_rate_pct || 0;
  // Color-code by performance: ≥30% emerald, ≥10% amber, else slate
  const tone = rate >= 30 ? "emerald" : rate >= 10 ? "amber" : "slate";
  return (
    <div
      className={`blast-perf-strip blast-perf-${tone}`}
      data-testid="blast-perf-strip"
      title={`Last 7 days · ${perf.sent_count} SMS blast${perf.sent_count === 1 ? "" : "s"} sent`}
    >
      <div className="blast-perf-cell">
        <Send size={11} className="opacity-70" />
        <span className="font-mono tabular-nums font-bold">{perf.sent_count}</span>
        <span className="opacity-70">sent</span>
      </div>
      <span className="blast-perf-sep" />
      <div className="blast-perf-cell">
        <Reply size={11} className="opacity-70" />
        <span className="font-mono tabular-nums font-bold">{perf.replied_count}</span>
        <span className="opacity-70">replies</span>
      </div>
      <span className="blast-perf-sep" />
      <div className="blast-perf-cell">
        <span className="font-mono tabular-nums font-extrabold text-base">{rate.toFixed(0)}%</span>
        <span className="opacity-70">rate</span>
      </div>
      {perf.top_buyer && (
        <>
          <span className="blast-perf-sep" />
          <div
            className="blast-perf-top"
            title={`${perf.top_buyer.name} · ${perf.top_buyer.replies}/${perf.top_buyer.sent} = ${perf.top_buyer.rate}%`}
            data-testid="blast-perf-top-buyer"
          >
            <Flame size={11} />
            <span className="font-bold truncate max-w-[100px]">{perf.top_buyer.name}</span>
          </div>
        </>
      )}
    </div>
  );
}

// ───── STAT STRIP ────────────────────────────────────────────────────────

function StatStrip({ summary }) {
  const cards = [
    { label: "Cash Buyers", value: summary.vetted, icon: CheckCircle2, accent: "#10B981", testid: "stat-vetted" },
    { label: "Captures", value: summary.captures, icon: Sparkles, accent: "#A855F7", testid: "stat-captures",
      sub: summary.captures_week > 0 ? `+${summary.captures_week} this week` : null },
    { label: "Hot (30d)", value: summary.hot, icon: Flame, accent: "#F59E0B", testid: "stat-hot" },
    { label: "Unread", value: summary.captures_new, icon: Zap, accent: "#0EA5E9", testid: "stat-unread" },
  ];
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="buyers-stat-strip">
      {cards.map((c) => (
        <div key={c.label} className="card buyers-stat" data-testid={c.testid}>
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center text-white shrink-0"
              style={{ background: c.accent }}
            >
              <c.icon size={18} />
            </div>
            <div>
              <div className="label-eyebrow">{c.label}</div>
              <div className="font-display font-extrabold text-2xl tabular-nums mt-0.5">{c.value}</div>
              {c.sub && <div className="text-[11px] text-emerald-600 font-semibold mt-0.5">{c.sub}</div>}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ───── BUYER CARD ────────────────────────────────────────────────────────

function BuyerCard({ buyer, onConvert, onChanged }) {
  const [from, to] = gradientForName(buyer.name);
  const isCapture = buyer.kind === "capture";
  const score = buyer.engagement_score || 0;
  const hot = score >= 60;

  const initial = (buyer.name || "?").trim().slice(0, 1).toUpperCase();
  const tone = sourceTone(buyer.utm_source);

  const buyBoxPills = useMemo(() => {
    const bb = buyer.buy_box || {};
    const out = [];
    if (bb.min_price || bb.max_price) {
      out.push({
        label: `${bb.min_price ? fmtMoney(bb.min_price) : "$0"} – ${bb.max_price ? fmtMoney(bb.max_price) : "any"}`,
        icon: DollarSign,
      });
    }
    if (bb.bedrooms) out.push({ label: `${bb.bedrooms}+ bd`, icon: Building2 });
    if (bb.property_types) out.push({ label: bb.property_types, icon: Building2 });
    return out;
  }, [buyer.buy_box]);

  const markContacted = async () => {
    try {
      await api.patch(`/captures/${buyer.id}/status`, { status: "contacted" });
      toast.success("Marked contacted");
      onChanged?.();
    } catch { toast.error("Couldn't update"); }
  };

  return (
    <div
      className={"buyer-card group" + (hot ? " hot" : "") + (isCapture ? " is-capture" : "")}
      data-testid={`buyer-card-${buyer.id}`}
    >
      {/* Hot streak ribbon */}
      {hot && (
        <div className="buyer-card-hot-ribbon" data-testid={`buyer-hot-${buyer.id}`}>
          <Flame size={10} /> HOT · {score}
        </div>
      )}
      {isCapture && (
        <div className="buyer-card-new-ribbon">
          <Sparkles size={10} /> NEW CAPTURE
        </div>
      )}

      {/* Avatar row */}
      <div className="flex items-start gap-3">
        <div
          className="buyer-avatar"
          style={{ background: `linear-gradient(135deg, ${from}, ${to})` }}
        >
          {initial}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-lg leading-tight truncate flex items-center gap-1.5">
            <span className="truncate">{buyer.name}</span>
            {buyer.consistent_responder && !isCapture && (
              <span
                className="buyer-card-responder-badge"
                title={`Replied to ${buyer.replies_30d} of your blasts in the last 30 days`}
                data-testid={`buyer-responder-${buyer.id}`}
              >
                <Reply size={9} /> RESPONDS
              </span>
            )}
          </div>
          {buyer.company ? (
            <div className="text-xs text-[var(--ink-2)] flex items-center gap-1 mt-0.5 truncate">
              <Building2 size={11} /> <span className="truncate">{buyer.company}</span>
            </div>
          ) : isCapture && buyer.source_lead_address ? (
            <div className="text-xs text-[var(--ink-3)] flex items-center gap-1 mt-0.5 truncate" title={buyer.source_lead_address}>
              <MapPin size={11} /> <span className="truncate">Re: {buyer.source_lead_address}</span>
            </div>
          ) : (
            <div className="text-xs text-[var(--ink-3)] mt-0.5">
              {timeAgo(buyer.last_activity_at || buyer.created_at)}
            </div>
          )}
        </div>
      </div>

      {/* Buy box / source pills */}
      <div className="flex flex-wrap gap-1.5 mt-3 min-h-[24px]">
        {buyBoxPills.length > 0 ? (
          buyBoxPills.map((p) => (
            <span key={p.label} className="buyer-pill" style={{ background: "#10B98114", color: "#047857", borderColor: "#10B98144" }}>
              <p.icon size={10} /> {p.label}
            </span>
          ))
        ) : isCapture ? (
          <span
            className="buyer-pill"
            style={{ background: tone.bg, color: tone.fg, borderColor: tone.border }}
          >
            <Sparkles size={10} /> {buyer.utm_source || "direct"}
          </span>
        ) : (
          <span className="buyer-pill" style={{ background: "#94A3B814", color: "#64748B", borderColor: "#94A3B844" }}>
            No buy box set
          </span>
        )}
      </div>

      {/* Engagement strip */}
      <div className="buyer-engagement">
        <EngagementChip
          icon={Eye} label="Views" value={buyer.views_30d || 0}
          tone={buyer.views_30d > 0 ? "active" : "muted"}
        />
        <EngagementChip
          icon={TrendingUp} label="Matched" value={buyer.deals_matched_count || 0}
          tone={buyer.deals_matched_count > 0 ? "active" : "muted"}
        />
        <EngagementChip
          icon={Reply} label="Replies" value={buyer.replies_30d || 0}
          tone={buyer.replies_30d >= 2 ? "fire" : buyer.replies_30d > 0 ? "warm" : "muted"}
        />
        <EngagementChip
          icon={Flame} label="Score" value={score}
          tone={hot ? "fire" : score > 0 ? "warm" : "muted"}
        />
      </div>

      {/* Contact links */}
      <div className="space-y-1 mt-3 text-sm">
        {buyer.phone && (
          <a
            href={`tel:${buyer.phone}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 text-[var(--ink-2)] hover:text-emerald-600 transition-colors"
            data-testid={`buyer-phone-${buyer.id}`}
          >
            <Phone size={12} /> {buyer.phone}
          </a>
        )}
        {buyer.email && (
          <a
            href={`mailto:${buyer.email}`}
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-2 text-[var(--ink-2)] hover:text-sky-600 transition-colors truncate"
          >
            <Mail size={12} /> <span className="truncate">{buyer.email}</span>
          </a>
        )}
      </div>

      {/* Capture-only message preview */}
      {isCapture && buyer.notes && (
        <div className="buyer-message">
          <MessageSquare size={11} className="mt-0.5 shrink-0 text-[var(--ink-3)]" />
          <span className="line-clamp-2">{buyer.notes}</span>
        </div>
      )}

      {/* Actions */}
      <div className="buyer-actions">
        {isCapture ? (
          <>
            {buyer.status === "new" && (
              <button
                type="button"
                className="btn-outline buyer-action-btn"
                onClick={markContacted}
                data-testid={`buyer-action-contacted-${buyer.id}`}
              >
                <CheckCircle2 size={12} /> Contacted
              </button>
            )}
            <button
              type="button"
              className="btn-money buyer-action-btn"
              onClick={onConvert}
              data-testid={`buyer-action-convert-${buyer.id}`}
            >
              <UserPlus size={12} /> Promote
            </button>
          </>
        ) : (
          <>
            <a
              href={`sms:${buyer.phone}`}
              className="btn-outline buyer-action-btn"
              data-testid={`buyer-action-sms-${buyer.id}`}
            >
              <MessageSquare size={12} /> Text
            </a>
            <Link
              to={`/app/buyers/${buyer.id}`}
              className="btn-outline buyer-action-btn"
              data-testid={`buyer-action-edit-${buyer.id}`}
            >
              <ArrowUpRight size={12} /> Open
            </Link>
          </>
        )}
      </div>
    </div>
  );
}

function EngagementChip({ icon: Icon, label, value, tone }) {
  const styles = {
    fire:   { bg: "linear-gradient(135deg,#FEF3C7,#FED7AA)", fg: "#B45309" },
    warm:   { bg: "#FEF3C7", fg: "#B45309" },
    active: { bg: "#ECFDF5", fg: "#047857" },
    muted:  { bg: "#F8FAFC", fg: "#94A3B8" },
  }[tone] || { bg: "#F8FAFC", fg: "#94A3B8" };
  return (
    <div className="buyer-eng-chip" style={{ background: styles.bg, color: styles.fg }}>
      <Icon size={10} />
      <span className="font-mono font-bold tabular-nums">{value}</span>
      <span className="opacity-70 text-[9px] uppercase tracking-wider">{label}</span>
    </div>
  );
}

function EmptyState({ tab, q }) {
  if (q) {
    return (
      <div className="card text-center py-16" data-testid="buyers-empty">
        <Search size={28} className="text-[var(--ink-3)] mx-auto mb-3" />
        <div className="font-display font-bold text-lg">No buyers match &ldquo;{q}&rdquo;</div>
        <p className="text-sm text-[var(--ink-2)] mt-2">Try a different name, phone, or city.</p>
      </div>
    );
  }
  if (tab === "capture") {
    return (
      <div className="card text-center py-16" data-testid="buyers-empty">
        <Sparkles size={28} className="text-[var(--ink-3)] mx-auto mb-3" />
        <div className="font-display font-bold text-lg">No new captures yet</div>
        <p className="text-sm text-[var(--ink-2)] mt-2 max-w-md mx-auto">
          Share a property report or post a yard-sign QR — buyers who drop their number show up here instantly.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="buyers-empty">
      <div className="card p-8 text-center">
        <Users2 size={32} className="text-[var(--ink-3)] mx-auto mb-3" />
        <div className="font-display font-extrabold text-xl">No buyers in your list yet</div>
        <p className="text-sm text-[var(--ink-2)] mt-2 max-w-md mx-auto">
          Add your first cash buyer manually — or share a property report and watch buyers flow in automatically.
        </p>
        <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
          <Link to="/app/team" className="btn-money inline-flex" data-testid="buyers-empty-cta">
            <Plus size={14} /> Add a Cash Buyer
          </Link>
          <Link to="/app/leads" className="btn btn-ghost inline-flex" data-testid="buyers-empty-cta-share">
            <Share2 size={14} /> Or share a deal
          </Link>
        </div>
      </div>

      {/* "How Buyers Grow" explainer — turns empty state into a coaching moment */}
      <div className="card p-6" data-testid="buyers-how-it-works">
        <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1 mb-3">
          <Zap size={11} /> How your buyer list grows
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <HowItWorksStep
            n={1}
            icon={UserPlus}
            tone="emerald"
            title="Add buyers you already know"
            body="Drop in your existing cash buyer rolodex with buy-box criteria (price, beds, markets). We auto-match every GO deal you analyze."
            cta="Add Cash Buyer"
            href="/app/team"
            ctaTestId="how-step-1-cta"
          />
          <HowItWorksStep
            n={2}
            icon={Share2}
            tone="sky"
            title="Share branded reports"
            body="Every public report you share has a capture form: 'Interested? Drop your number.' Strangers opt into your buyer list automatically."
            cta="Open a Lead"
            href="/app/leads"
            ctaTestId="how-step-2-cta"
          />
          <HowItWorksStep
            n={3}
            icon={QrCode}
            tone="violet"
            title="Drop yard-sign QR codes"
            body="Each report generates a QR you can print on signs, postcards, or business cards. Anyone who scans → lands on your capture page."
            cta="See your reports"
            href="/app/leads"
            ctaTestId="how-step-3-cta"
          />
        </div>
        <div className="mt-5 pt-4 border-t border-[var(--line)] flex items-start gap-2 text-xs text-[var(--ink-2)]">
          <Sparkles size={13} className="text-amber-500 shrink-0 mt-0.5" />
          <div className="leading-snug">
            <strong>The compounding magic:</strong> Every deal you share doubles as a buyer-acquisition tool.
            Most active wholesalers build a 200-500 person cash buyer list within 6 months — with zero cold outreach.
          </div>
        </div>
      </div>
    </div>
  );
}


function HowItWorksStep({ n, icon: Icon, tone, title, body, cta, href, ctaTestId }) {
  const palette = {
    emerald: { bg: "bg-emerald-50", border: "border-emerald-200", chipBg: "bg-emerald-600", text: "text-emerald-700" },
    sky:     { bg: "bg-sky-50",     border: "border-sky-200",     chipBg: "bg-sky-600",     text: "text-sky-700" },
    violet:  { bg: "bg-violet-50",  border: "border-violet-200",  chipBg: "bg-violet-600",  text: "text-violet-700" },
  }[tone] || { bg: "bg-stone-50", border: "border-stone-200", chipBg: "bg-stone-600", text: "text-stone-700" };

  return (
    <div className={`rounded-lg border p-4 ${palette.bg} ${palette.border} flex flex-col`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`${palette.chipBg} text-white rounded-full w-6 h-6 flex items-center justify-center font-bold text-xs`}>
          {n}
        </div>
        <Icon size={14} className={palette.text} />
      </div>
      <div className={`font-bold text-sm ${palette.text}`}>{title}</div>
      <p className="text-xs text-[var(--ink-2)] mt-1 leading-snug flex-1">{body}</p>
      <Link
        to={href}
        className={`text-xs font-bold mt-3 inline-flex items-center gap-1 ${palette.text} hover:underline`}
        data-testid={ctaTestId}
      >
        {cta} <ArrowRight size={11} />
      </Link>
    </div>
  );
}
